import AddIcon from '@mui/icons-material/Add';
import DoneIcon from '@mui/icons-material/Done';
import { Box, TextField, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommunicationDTO, PROJECT_NAME } from 'utils';
import { AccordionCard } from '../../../../../components/AccordionCard';
import { ActionsList } from '../../../../../components/ActionsList';
import { DeleteIconButton } from '../../../../../components/DeleteIconButton';
import { RoundedButton } from '../../../../../components/RoundedButton';
import { useGetAppointmentAccessibility } from '../../hooks/useGetAppointmentAccessibility';
import { useSavePatientInstruction } from '../../stores/appointment/appointment.queries';
import { useChartData, useDeleteChartData, useSaveChartData } from '../../stores/appointment/appointment.store';
import { PatientInstructionsTemplatesDialog } from './components/PatientInstructionsTemplatesDialog';

export const PatientInstructionsCard: FC = () => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [myTemplatesOpen, setMyTemplatesOpen] = useState(false);
  const [defaultTemplatesOpen, setDefaultTemplatesOpen] = useState(false);
  const [instruction, setInstruction] = useState('');
  const { mutate: savePatientInstruction, isPending: isSavePatientInstructionLoading } = useSavePatientInstruction();
  const { mutate: saveChartData, isPending: isSaveChartDataLoading } = useSaveChartData();
  const { mutate: deleteChartData } = useDeleteChartData();
  const isLoading = isSavePatientInstructionLoading || isSaveChartDataLoading;
  const { isAppointmentReadOnly: isReadOnly } = useGetAppointmentAccessibility();
  const { chartData, setPartialChartData } = useChartData();
  const instructions = chartData?.instructions || [];

  const onAddAndSave = (): void => {
    savePatientInstruction(
      { text: instruction },
      {
        onError: () => {
          enqueueSnackbar(t('telemed.plan.patientInstructions.errorSaveTemplate'), {
            variant: 'error',
          });
        },
      }
    );
    onAdd();
  };

  const onAdd = (): void => {
    const localInstructions = [...instructions, { text: instruction }];

    setPartialChartData({
      instructions: localInstructions,
    });
    saveChartData(
      {
        instructions: [{ text: instruction }],
      },
      {
        onSuccess: (data) => {
          const instruction = (data?.chartData?.instructions || [])[0];
          if (instruction) {
            setPartialChartData({
              instructions: localInstructions.map((item) => (item.resourceId ? item : instruction)),
            });
          }
        },
        onError: () => {
          enqueueSnackbar(t('telemed.plan.patientInstructions.errorAdd'), {
            variant: 'error',
          });
          setPartialChartData({ instructions });
          setInstruction(instruction);
        },
      }
    );

    setInstruction('');
  };

  const onDelete = (value: CommunicationDTO): void => {
    setPartialChartData({
      instructions: instructions.filter((item) => item.resourceId !== value.resourceId),
    });
    deleteChartData(
      {
        instructions: [value],
      },
      {
        onError: () => {
          enqueueSnackbar(t('telemed.plan.patientInstructions.errorDelete'), {
            variant: 'error',
          });
          setPartialChartData({ instructions });
        },
      }
    );
  };

  return (
    <>
      <AccordionCard
        label={t('telemed.plan.patientInstructions.title')}
        collapsed={collapsed}
        onSwitch={() => setCollapsed((prevState) => !prevState)}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {!isReadOnly && (
            <>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <TextField
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  size="small"
                  label={t('telemed.plan.patientInstructions.label')}
                  placeholder={t('telemed.plan.patientInstructions.placeholder', { projectName: PROJECT_NAME })}
                  multiline
                  fullWidth
                />
                <RoundedButton onClick={onAdd} disabled={!instruction.trim() || isLoading} startIcon={<AddIcon />}>
                  {t('telemed.plan.patientInstructions.add')}
                </RoundedButton>
                <RoundedButton
                  onClick={onAddAndSave}
                  disabled={!instruction.trim() || isLoading}
                  startIcon={<DoneIcon />}
                >
                  {t('telemed.plan.patientInstructions.addAndSave')}
                </RoundedButton>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <RoundedButton onClick={() => setMyTemplatesOpen(true)}>
                  {t('telemed.plan.patientInstructions.myTemplates')}
                </RoundedButton>
                <RoundedButton onClick={() => setDefaultTemplatesOpen(true)}>
                  {t('telemed.plan.patientInstructions.orgTemplates', { projectName: PROJECT_NAME })}
                </RoundedButton>
              </Box>
            </>
          )}

          {instructions.length > 0 && (
            <ActionsList
              data={instructions}
              getKey={(value, index) => value.resourceId || index}
              renderItem={(value) => <Typography style={{ whiteSpace: 'pre-line' }}>{value.text}</Typography>}
              renderActions={
                isReadOnly
                  ? undefined
                  : (value) => <DeleteIconButton disabled={!value.resourceId} onClick={() => onDelete(value)} />
              }
              divider
              gap={1}
            />
          )}

          {instructions.length === 0 && isReadOnly && (
            <Typography color="secondary.light">{t('telemed.plan.patientInstructions.notProvided')}</Typography>
          )}
        </Box>
      </AccordionCard>

      {myTemplatesOpen && (
        <PatientInstructionsTemplatesDialog
          open={myTemplatesOpen}
          onClose={() => setMyTemplatesOpen(false)}
          type="provider"
          onSelect={(value) => setInstruction(value.text!)}
        />
      )}
      {defaultTemplatesOpen && (
        <PatientInstructionsTemplatesDialog
          open={defaultTemplatesOpen}
          onClose={() => setDefaultTemplatesOpen(false)}
          type="organization"
          onSelect={(value) => setInstruction(value.text!)}
        />
      )}
    </>
  );
};
